import { useEffect, useRef } from 'react';

const CHECK_INTERVAL_MS = 60 * 1000; // check every minute
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];

/**
 * Tracks user activity and invokes a callback after a period of inactivity.
 * Single responsibility: "last activity" + "after N minutes inactive, call onInactive".
 *
 * @param {Object} options
 * @param {number} [options.inactivityMinutes=60] - Minutes without activity before callback.
 * @param {() => void} [options.onInactive] - Called when inactivity threshold is reached.
 * @param {boolean} [options.enabled=true] - When false, activity is not tracked and callback is never called.
 */
export function useInactivityLogout({
  inactivityMinutes = 60,
  onInactive = () => {},
  enabled = true,
} = {}) {
  const lastActivityAt = useRef(Date.now());
  const intervalId = useRef(null);
  const onInactiveRef = useRef(onInactive);
  onInactiveRef.current = onInactive;

  useEffect(() => {
    if (!enabled) {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
      return;
    }

    const thresholdMs = inactivityMinutes * 60 * 1000;

    function touchActivity() {
      lastActivityAt.current = Date.now();
    }

    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, touchActivity, { passive: true });
    });
    lastActivityAt.current = Date.now();

    intervalId.current = setInterval(() => {
      if (Date.now() - lastActivityAt.current >= thresholdMs) {
        if (intervalId.current) {
          clearInterval(intervalId.current);
          intervalId.current = null;
        }
        onInactiveRef.current();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => {
        window.removeEventListener(ev, touchActivity);
      });
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, [enabled, inactivityMinutes]);

  return null;
}
